/**
 * One statement, made after every payout: *here is what you earned.*
 *
 * ── Reusable by construction, not by intention ───────────────────────────────
 *
 * Two things pay a player: finishing a daily quest, and finishing a battle. They
 * have nothing else in common — one is a claim the player pressed a button for,
 * the other is a session the server closed after the socket went away — and the
 * only way one component can serve both without becoming a switch statement is
 * for its interface to be about **rewards** and nothing else.
 *
 * So a request is *a heading, a subject and a list of amounts*. It has no
 * `questId`, no `sessionId`, no `kind`, and no field either caller could use to
 * ask for special treatment. Neither caller knows the other exists, and a third
 * one — a rank-up, a login streak, a refund — costs a call rather than a branch.
 *
 * That is deliberately the *opposite* of what a "quest reward popup" and a
 * "battle summary popup" sharing a base component would have been: two shapes,
 * one of them optional everywhere, and a template full of `v-if`.
 *
 * ── The promise is the whole API ─────────────────────────────────────────────
 *
 * `showRewards` resolves when the player dismisses it, so a call site reads:
 *
 *     await showRewards({ heading: 'Quest complete', subject: quest.name, rewards });
 *
 * and keeps its own control flow. This is `state/confirm-purchase.ts` one step
 * along, and for the same reasons — a store that remembered a pending *action*
 * would mean every payout being expressible as data, which is how a dialog ends
 * up knowing about the shop. The only difference is that there is nothing to
 * decline: the money has already moved, and this is a receipt rather than a
 * question. It resolves `void`, never a boolean, so no call site can mistake it
 * for a decision.
 *
 * ── One at a time ────────────────────────────────────────────────────────────
 *
 * A second report while one is open settles the first and replaces it. Queueing
 * would stack receipts at somebody who has moved on; dropping would leave the
 * first `await` pending forever and wedge whatever came after it. Since neither
 * one is a question, replacing loses nothing but a glance.
 */
import { computed, ref } from 'vue';

/** How a number is written. The popup owns the formatting; callers pass amounts. */
export type RewardKind = 'credits' | 'xp' | 'diamonds' | 'plain';

export interface Reward {
  /** What it is, in the player's words: "Credits", "Experience", "Kills". */
  label: string;
  amount: number;
  kind: RewardKind;
  /**
   * A figure that is worth stating but was not *earned* — a progress count
   * beside a payout, for instance.
   *
   * It is drawn quietly and without a plus sign, which is the whole reason it
   * exists: a receipt that listed "3 deaths" in the same green as "600 credits"
   * would be claiming somebody was paid in deaths.
   */
  informational?: boolean;
}

export interface RewardReport {
  /** The band across the top: "Quest complete". */
  heading: string;
  /** What it was for: the quest's name, the battle's map. */
  subject: string;
  /** A quieter line under the subject. Optional. */
  detail?: string;
  rewards: Reward[];
  /** Overrides the dismiss button's label. */
  dismissLabel?: string;
}

interface Pending extends RewardReport {
  settle: () => void;
}

const pending = ref<Pending | null>(null);

/** The report on screen, or `null`. Read by `ui/RewardPopup.vue`. */
export const rewardReport = computed<RewardReport | null>(() =>
  pending.value
    ? {
        heading: pending.value.heading,
        subject: pending.value.subject,
        detail: pending.value.detail,
        rewards: pending.value.rewards,
        dismissLabel: pending.value.dismissLabel,
      }
    : null,
);

/**
 * Shows what was earned. Resolves when it is dismissed.
 *
 * Never rejects, for the same reason `askToBuy` does not: a receipt that threw
 * would abort the path after a payout that has already happened.
 */
export function showRewards(report: RewardReport): Promise<void> {
  pending.value?.settle();
  return new Promise<void>((resolve) => {
    pending.value = {
      ...report,
      settle: () => {
        pending.value = null;
        resolve();
      },
    };
  });
}

/** Closes the open report — the button, the backdrop, Escape and Return. */
export function dismissRewards(): void {
  pending.value?.settle();
}

/**
 * Test seam: abandons anything open without leaving a promise pending.
 *
 * Settles rather than drops, exactly as `resetPurchaseConfirm` does — a suite
 * that left one pending would hang the next test that awaited it.
 */
export function resetRewards(): void {
  pending.value?.settle();
}
